import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { UserCircle2, X } from 'lucide-react';
import {
  clearPersistedWorkbenchPersona,
  useWorkbenchStore,
} from '@/stores/workbench';
import { useClientStore } from '@/stores/client';
import { personasApi, type ClientPersonaSummary } from '@/services/api';

/**
 * Small teal persona indicator rendered in the TopBar, to the left of the
 * client selector. Only visible on /creative routes when a persona is active
 * in the workbench context. The main link goes to Creative Home to change
 * persona; the ✕ clears URL `persona`, localStorage workbench snapshot, and
 * the global workbench store (TopBar sits outside CreativeWorkbenchProvider).
 */
export function WorkbenchPersonaChip() {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const personaId = useWorkbenchStore((s) => s.personaId);
  const clientUid = useClientStore((s) => s.selectedClientUid);
  const [personas, setPersonas] = useState<ClientPersonaSummary[]>([]);

  const isCreativeRoute = pathname.startsWith('/creative');

  const handleClearPersona = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      clearPersistedWorkbenchPersona();
      const next = new URLSearchParams(searchParams);
      next.delete('persona');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (!clientUid || !personaId || !isCreativeRoute) return;
    let cancelled = false;
    personasApi
      .listForClient(clientUid)
      .then((res) => {
        if (!cancelled) setPersonas(res.personas ?? []);
      })
      .catch(() => {
        if (!cancelled) setPersonas([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, personaId, isCreativeRoute]);

  if (!isCreativeRoute || !personaId) return null;

  const personaName = personas.find((p) => p.id === personaId)?.name ?? null;

  return (
    <div className="flex items-center gap-0.5 pl-2.5 pr-1 py-1 rounded-full bg-teal-50 border border-teal-200 text-[11px] font-medium text-teal-800 shrink-0">
      <Link
        to="/creative"
        title="Persona context active — click to change"
        className="flex items-center gap-1.5 min-w-0 hover:bg-teal-100/80 rounded-l-full -my-1 py-1 pl-0 pr-1.5 -ml-0.5 transition-colors"
      >
        <span
          className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0"
          aria-hidden
        />
        <UserCircle2 size={12} className="shrink-0" aria-hidden />
        <span className="max-w-[140px] truncate">{personaName ?? personaId}</span>
      </Link>
      <button
        type="button"
        onClick={handleClearPersona}
        className="flex items-center justify-center rounded-full p-0.5 text-teal-700 hover:bg-teal-200/80 hover:text-teal-900 transition-colors"
        title="Clear persona from workbench (URL + saved context)"
        aria-label="Clear persona from workbench"
      >
        <X size={12} strokeWidth={2.5} aria-hidden />
      </button>
    </div>
  );
}
